import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import SECTION_META from "../helper/sectionMeta";
import { useOpenSectionList, useTotalOpenCards } from "./sheetCounts";
import "../styles/unsavedCardsGuard.scss";

/**
 * ZY-10350 — leave-patient warning for unsaved editor cards.
 *
 * Mounted inside PatientShell. Watches the shell-level SheetCountsProvider
 * for any section with open editor cards and, whenever the total is > 0:
 *
 *   • Attaches a `beforeunload` handler so the browser's own confirm
 *     fires on close / refresh / URL-bar navigation. Browsers won't let
 *     us style this one or list the section names — the generic text is
 *     all the UA shows, so we rely on the custom modal below for in-app
 *     navigation where we DO own the UX.
 *   • Attaches a `history.block` callback so a React-Router v5 push /
 *     replace / POP inside the SPA pops OUR modal (listing every
 *     section with pending cards) and defers the transition until the
 *     reviewer decides. "Ignore and leave" unblocks + replays the
 *     transition; "Cancel" closes the modal and leaves the block in
 *     place.
 *
 * Both coexist with ActiveCallProvider's narrower beforeunload (which
 * fires only while a call is live) — browsers run all registered
 * beforeunloads and still only show one dialog to the user.
 *
 * Some actions discard open cards in ways other than leaving, so they ask
 * BEFORE they act, through confirmDiscardOpenCards. phr's "Update booking for"
 * is one: it saves, then remounts the screen. Asking only when the remount
 * navigated meant the mapping was already saved, so Cancel left the screen on
 * the previous member (product, 2026-10-09).
 */

/* The mounted guard's way to ask, or null. Module-level because the askers sit
   outside PatientShell (the header's booking-for control), and so outside the
   counts' provider. */
let askMounted = null;

/** Asks before an action that would discard the open cards. Resolves true
    when none are open, or when the doctor chooses to discard them; false on
    Cancel. With no guard mounted (phones, or before the shell renders) no
    cards are counted, so true. `what` completes "… will discard any unsaved
    changes."; `confirmLabel` is the button that goes ahead. */
export const confirmDiscardOpenCards = ({ what, confirmLabel }) =>
  askMounted ? askMounted({ what, confirmLabel }) : Promise.resolve(true);

/* Runs `navigate` past the guard, for a transition the doctor has ALREADY
   confirmed. history.block calls the guard synchronously inside push/replace,
   so the flag is up for that one call only. */
let unguarded = false;
export const withoutUnsavedCardsGuard = (navigate) => {
  unguarded = true;
  try {
    navigate();
  } finally {
    unguarded = false;
  }
};

const UnsavedCardsGuard = () => {
  const history = useHistory();
  const total = useTotalOpenCards();
  const openSections = useOpenSectionList();
  const totalRef = useRef(total);
  totalRef.current = total;

  // A question from confirmDiscardOpenCards: { what, confirmLabel, resolve }.
  const [asking, setAsking] = useState(null);
  const askingRef = useRef(null);
  askingRef.current = asking;
  useEffect(() => {
    const ask = (copy) =>
      new Promise((resolve) => {
        if (totalRef.current === 0) {
          resolve(true);
          return;
        }
        // A second question replaces the first, which counts as Cancel.
        if (askingRef.current) askingRef.current.resolve(false);
        setAsking({ ...copy, resolve });
      });
    askMounted = ask;
    return () => {
      if (askMounted === ask) askMounted = null;
      // Torn down mid-question: nothing was confirmed.
      if (askingRef.current) askingRef.current.resolve(false);
    };
  }, []);
  const answer = useCallback((go) => {
    const q = askingRef.current;
    setAsking(null);
    if (q) q.resolve(go);
  }, []);

  // Native beforeunload. Only attached while there's something to warn
  // about so a reviewer with no open cards doesn't see a spurious prompt
  // on a plain tab close.
  useEffect(() => {
    if (total === 0) return undefined;
    const onBeforeUnload = (e) => {
      // Chrome ignores the custom message but needs preventDefault AND
      // a returnValue assignment to fire its native dialog; Firefox /
      // Safari behave similarly. The text, if a browser ever surfaces
      // it, is the generic "unsaved" copy.
      e.preventDefault();
      e.returnValue = "You have unsaved editor cards. Leave anyway?";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [total]);

  // In-app history guard. Capture the pending transition and show the
  // modal; the `unblockRef` lets Confirm temporarily lift the block so
  // the follow-up push/goBack doesn't re-prompt itself.
  const [pending, setPending] = useState(null);
  const unblockRef = useRef(null);

  useEffect(() => {
    if (total === 0) return undefined;
    const unblock = history.block((location, action) => {
      // Already confirmed by the doctor (withoutUnsavedCardsGuard).
      if (unguarded) return true;
      setPending({ location, action });
      // Returning false suppresses React Router's built-in `prompt`
      // dialog and keeps the transition blocked. Our modal drives the
      // decision from here.
      return false;
    });
    unblockRef.current = unblock;
    return () => {
      unblock();
      unblockRef.current = null;
    };
  }, [history, total]);

  const closeModal = useCallback(() => setPending(null), []);

  const confirm = useCallback(() => {
    const tx = pending;
    if (!tx) return;
    // Lift the block before replaying the transition so React Router
    // doesn't fire us again. If the editor cards still exist after the
    // navigation, the deps-driven effect above re-establishes a fresh
    // block on the next render.
    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }
    setPending(null);
    if (tx.action === "POP") {
      history.goBack();
      return;
    }
    const { pathname, search, hash, state } = tx.location || {};
    const path = `${pathname || ""}${search || ""}${hash || ""}`;
    if (tx.action === "REPLACE") {
      history.replace(path, state);
    } else {
      history.push(path, state);
    }
  }, [history, pending]);

  // Rows shown in the modal — resolve section keys to display names via
  // SECTION_META so the dialog reads "Chief complaints · 2" instead of
  // "chief-complaints · 2". Unknown keys fall back to the raw key so a
  // mis-published section is still visible rather than silently hidden.
  const rows = useMemo(
    () =>
      openSections.map(([key, count]) => ({
        key,
        count,
        name: (SECTION_META[key] && SECTION_META[key].name) || key,
      })),
    [openSections],
  );

  // One dialog for both: a held navigation, or a question asked before an
  // action. Only the sentence's subject and the go-ahead button differ.
  const dialog = asking
    ? { what: asking.what, confirmLabel: asking.confirmLabel, cancel: () => answer(false), go: () => answer(true) }
    : pending
    ? { what: "Leaving now", confirmLabel: "Ignore and leave", cancel: closeModal, go: confirm }
    : null;
  if (!dialog) return null;

  return (
    <div className="ucg-backdrop" role="presentation" onClick={dialog.cancel}>
      <div
        className="ucg-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ucg-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="ucg-title" className="ucg-title">Unsaved editor cards</h2>
        <p className="ucg-sub">
          You have {total === 1 ? "an open card" : `${total} open cards`} in the
          following section{rows.length === 1 ? "" : "s"}. {dialog.what} will
          discard any unsaved changes.
        </p>
        <ul className="ucg-list">
          {rows.map((r) => (
            <li key={r.key} className="ucg-list-row">
              <span className="ucg-list-name">{r.name}</span>
              <span className="ucg-list-count">{r.count}</span>
            </li>
          ))}
        </ul>
        <div className="ucg-foot">
          <button type="button" className="ucg-btn ucg-btn-ghost" onClick={dialog.cancel}>
            Cancel
          </button>
          <button type="button" className="ucg-btn ucg-btn-danger" onClick={dialog.go}>
            {dialog.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnsavedCardsGuard;
